import { CornerDownLeft, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../features/auth/AuthProvider';
import { filterCommands } from './commandSearch';
import { navItems, visibleItems, type NavItem } from './navigation';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: Props) {
  if (!open) return null;
  return <PaletteDialog onClose={onClose} />;
}

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const { can, session } = useAuth();
  const clientPerson = Boolean(session?.user.client_id);
  const platform = Boolean(session?.tenant.platform);
  const allowed = useMemo(
    () => visibleItems(navItems, can, clientPerson, platform),
    [can, clientPerson, platform],
  );
  const results = useMemo(() => filterCommands(allowed, query), [allowed, query]);
  const activeIndex = Math.min(active, Math.max(results.length - 1, 0));

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => previous?.focus();
  }, []);

  const select = (item: NavItem | undefined) => {
    if (!item) return;
    onClose();
    void navigate(item.to);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive((activeIndex + 1) % Math.max(results.length, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((activeIndex - 1 + results.length) % Math.max(results.length, 1));
        break;
      case 'Enter':
        e.preventDefault();
        select(results[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      case 'Tab':
        // Focus stays in the dialog: the input is its only tabbable element.
        e.preventDefault();
        break;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paleta de comandos"
        onKeyDown={onKeyDown}
        className="relative w-full max-w-lg overflow-hidden rounded-lg border border-line-strong bg-surface-raised shadow-2xl shadow-black/50"
      >
        <div className="flex items-center gap-2.5 border-b border-line px-3.5">
          <Search aria-hidden="true" className="size-4 text-fg-subtle" strokeWidth={2} />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={
              results[activeIndex] ? `${listId}-${results[activeIndex].to}` : undefined
            }
            aria-autocomplete="list"
            aria-label="Buscar comando"
            placeholder="Ir para…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            className="h-11 flex-1 bg-transparent text-sm text-fg placeholder:text-fg-subtle focus:outline-none"
          />
          <kbd className="rounded border border-line px-1.5 font-mono text-2xs text-fg-subtle">
            Esc
          </kbd>
        </div>
        <ul
          id={listId}
          role="listbox"
          aria-label="Resultados"
          className="max-h-80 overflow-y-auto p-1.5"
        >
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-[13px] text-fg-subtle">
              Nenhum resultado para “{query}”
            </li>
          )}
          {results.map((item, i) => {
            const Icon = item.icon;
            const selected = i === activeIndex;
            return (
              <li
                key={item.to}
                id={`${listId}-${item.to}`}
                role="option"
                aria-selected={selected}
                onMouseMove={() => {
                  setActive(i);
                }}
                onClick={() => {
                  select(item);
                }}
                className={`flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 ${
                  selected ? 'bg-accent-soft' : ''
                }`}
              >
                <Icon
                  aria-hidden="true"
                  className={`size-4 shrink-0 ${selected ? 'text-accent' : 'text-fg-subtle'}`}
                  strokeWidth={1.75}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-fg">{item.label}</div>
                  <div className="truncate text-2xs text-fg-subtle">{item.summary}</div>
                </div>
                {selected && (
                  <CornerDownLeft aria-hidden="true" className="size-3.5 text-fg-subtle" />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
